import { durationInMinutes, now, nowMillis } from "@/lib/date-utils"
import { mtLocation } from "@/lib/mt-location"
import {
  shouldDeferPostSignupPrompts,
  skipClaimHandlePromptForPendingSetup,
} from "@/lib/pending-auth-action"
import { webStorage } from "@/lib/web-storage"
import { selectSessionStatus } from "./auth-slice"
import { showEmailVerifyStep } from "./auth-thunks"
import { selectHasOpenDialog } from "./dialog-slice"
import {
  mtCreateAsyncThunk,
  unwrapWithError,
} from "./enhanced-create-async-thunk"
import { AppDispatch, RootState } from "./redux-store"
import { runPendingSaveAction } from "./saved-thunks"
import userSlice, {
  selectEmailVerifyMode,
  selectProfileSetupDialogOpen,
  selectShowClaimHandleDialog,
  selectUser,
} from "./user-slice"
import { selectVoiceModeOpen } from "./voice-chat-slice"

// Pages that only exist to hand off somewhere else. A prompt raised here is torn
// down, losing anything typed into it, the moment the real destination loads.
const HANDOFF_PATHS = [
  "/auth",
  "/join-collection",
  "/join-organization",
  "/join-trip",
  "/verify",
]

// A day is also at most once a session, and leaves an unverified account usable
// in between.
const NUDGE_INTERVAL_MS = 24 * 60 * 60 * 1000

function isHandoffPath(pathname: string) {
  return HANDOFF_PATHS.some((path) => pathname.startsWith(path))
}

function millisSinceLastPrompt() {
  const promptedAt = Number(
    webStorage.getItem("emailVerificationPromptedAt") ?? 0
  )
  return nowMillis() - (Number.isFinite(promptedAt) ? promptedAt : 0)
}

/*
 * Every prompt we raise of our own accord goes through here, so one of them
 * resets the clock on the rest. The profile-header Verify button is the user
 * asking us, and deliberately does not count.
 */
export const promptEmailVerification = mtCreateAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>("postSignup/promptEmailVerification", async (_, { dispatch }) => {
  // Stamped before the request so two triggers arriving together can't both prompt
  const promptedAt = nowMillis().toString()
  webStorage.setItem("emailVerificationPromptedAt", promptedAt)

  try {
    await unwrapWithError(dispatch(showEmailVerifyStep()))
  } catch (error) {
    // A mail the backend refused to send shouldn't cost the user their day. Clear
    // only our own stamp: a prompt that has since succeeded keeps its place.
    if (webStorage.getItem("emailVerificationPromptedAt") === promptedAt) {
      webStorage.removeItem("emailVerificationPromptedAt")
    }
    throw error
  }
})

/*
 * Prompts a freshly signed up user to claim their handle, then to verify their
 * email. Every call re-reads state, so dispatching it again is harmless.
 */
export const showPostSignupPromptsIfNecessary = mtCreateAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>("postSignup/showPromptsIfNecessary", async (_, { dispatch, getState }) => {
  const state = getState()
  const currentUser = selectUser(state)

  if (
    selectSessionStatus(state) !== "authenticated" ||
    selectHasOpenDialog(state) ||
    selectShowClaimHandleDialog(state) ||
    currentUser.provider === "anonymous" ||
    isHandoffPath(mtLocation.pathname)
  ) {
    return
  }

  // The pending action is consumed as setup opens. Record this prompt as
  // intentionally skipped so it cannot appear over or immediately after
  // the setup flow.
  const deferPostSignupPrompts = skipClaimHandlePromptForPendingSetup()

  const shouldShowClaimHandle =
    !deferPostSignupPrompts &&
    !webStorage.getItem("seenClaimHandleDialog") &&
    // only newly created users
    durationInMinutes(currentUser.created_at, now()) < 5

  const shouldShowEmailVerify =
    !selectProfileSetupDialogOpen(state) &&
    !deferPostSignupPrompts &&
    selectEmailVerifyMode(state) &&
    !currentUser.email_validated &&
    currentUser.provider === "email"

  // The claim handle dialog comes first. If reserved handles ever need the other
  // order, verification should lead only for email providers eligible for one
  if (shouldShowClaimHandle) {
    dispatch(userSlice.actions.setShowClaimHandleDialog(true))
    return
  }

  if (shouldShowEmailVerify) {
    dispatch(userSlice.actions.setEmailVerifyMode(false))
    await dispatch(promptEmailVerification())
    return
  }

  await dispatch(runPendingSaveAction())
})

/*
 * Asks an unverified account to verify, on the way out of something the user
 * chose to do. Signing up into the assistant setup skips verification on
 * purpose, so for most accounts this is what eventually asks.
 */
export const nudgeEmailVerificationIfDue = mtCreateAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>("postSignup/nudgeEmailVerification", async (_, { dispatch, getState }) => {
  const state = getState()
  const currentUser = selectUser(state)

  if (
    selectSessionStatus(state) !== "authenticated" ||
    // only email signups have anything to verify
    currentUser.provider !== "email" ||
    currentUser.email_validated ||
    selectHasOpenDialog(state) ||
    selectShowClaimHandleDialog(state) ||
    selectProfileSetupDialogOpen(state) ||
    // a modal mid-conversation would take the microphone away
    selectVoiceModeOpen(state) ||
    // setup is still ahead of them and gets the uninterrupted run
    shouldDeferPostSignupPrompts() ||
    isHandoffPath(mtLocation.pathname) ||
    millisSinceLastPrompt() < NUDGE_INTERVAL_MS
  ) {
    return
  }

  await dispatch(promptEmailVerification())
})
